import { RefObject, useEffect, useState } from 'react'

import { useResizeObserver } from '@/hooks/use-resize-observer/hook'
import type { Size } from '@/hooks/use-resize-observer/hook'

/**
 * Keeps the Production list on the performer page in a proper position.
 * This stops the user from ending up at the footer of the page when collapsing
 * the production list.
 * @param showingMoreProductions
 * @returns refs to the production list and show more button that can be set
 */
const useScrollOnListCollapse = (
    showingMoreProductions: boolean,
    buttonRef: RefObject<HTMLButtonElement>,
    listRef: RefObject<HTMLDivElement>,
) => {
    // Used to prevent automatic scrolling until a user has interacted with the button
    const [hasInteractedWithButton, setHasInteractedWithButton] = useState(false)

    const [closedListHeight, setClosedListHeight] = useState(0)
    const [openedListHeight, setOpenedListHeight] = useState(0)

    const heightDiff = Math.max(0, openedListHeight - closedListHeight)

    useEffect(() => {
        if (!buttonRef.current) {
            return
        }

        const buttonElement = buttonRef.current
        const buttonListener = () => {
            if (hasInteractedWithButton) {
                return
            }
            setHasInteractedWithButton(true)
        }

        buttonElement.addEventListener('click', buttonListener)

        return () => buttonElement?.removeEventListener('click', buttonListener)
    }, [hasInteractedWithButton, buttonRef])

    const onListResize = (size: Size) => {
        if (showingMoreProductions) {
            setOpenedListHeight(size.height ?? 0)
        } else {
            setClosedListHeight(size.height ?? 0)
        }
    }

    useResizeObserver({
        ref: listRef,
        onResize: onListResize,
    })

    useEffect(() => {
        if (!hasInteractedWithButton) {
            return
        }

        // upon clicking 'show less'
        if (!showingMoreProductions) {
            // Mimic old behavior. Stops the user from being disoriented when collapsing the list
            window.scrollTo({ behavior: 'instant' as ScrollBehavior, top: window.scrollY - heightDiff })
            buttonRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
    }, [buttonRef, hasInteractedWithButton, heightDiff, showingMoreProductions])
}

export default useScrollOnListCollapse
